<template>
  <div class="input-editor-container" :class="{ error: hasError }">
    <div class="email-input-container input-container">
      <FriedIcon class="icon-left" icon="mail-sign-at" size="small" />
      <EditorContent
        class="email-input"
        data-external="doc-view-email-input"
        :editor="editor"
        spellcheck="false"
      />
    </div>
    <FriedSpinner v-if="isUpdating" class="updating-spinner" :size="20" />
    <FriedParagraph v-if="hasError" :id="errorMessageId" class="error-message" size="small">
      {{ errorMessage }}
    </FriedParagraph>
  </div>
</template>
<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-3';
import Focus from '@tiptap/extension-focus';
import Text from '@tiptap/extension-text';
import type { Field, MergeValues } from '@getaccept/editor-lib';
import {
  ProsemirrorHelper,
  InputDocNode,
  MergeValueNode,
  ParagraphNode,
  EditorHelper,
} from '@getaccept/editor-lib';
import type { ShallowRef } from 'vue';
import { onBeforeUnmount, onMounted, computed, ref, watch } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import Placeholder from '@tiptap/extension-placeholder';
import { validateEmail } from '@getaccept/lib-shared/src/helpers';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';

const {
  inputField,
  recipientInput,
  fieldFocused,
  editable,
  fields = [],
  isUpdating,
  isRequired,
  mergeValues,
  hasUpdateError,
  id,
  ariaLabelledby,
  ariaDescribedby,
} = defineProps<{
  inputField: Field;
  recipientInput?: EditorRecipientInput;
  fieldFocused?: boolean;
  editable?: boolean;
  isUpdating?: boolean;
  isRequired?: boolean;
  mergeValues: MergeValues;
  fields?: Field[];
  hasUpdateError?: boolean;
  id?: string;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
}>();

const errorMessageId = computed(() => `${id || 'email-input'}-error-message`);
const inputId = computed(() => id || 'email-input');

const emit = defineEmits<{
  update: [string];
  'validate-form-input': [boolean];
}>();

const editor = ref<ShallowRef<TipTapEditor> | undefined>(undefined);
const emailErrorMessage = ref('');

const hasError = computed(() => hasUpdateError || !!emailErrorMessage.value);
const errorMessage = computed(() => emailErrorMessage.value || '');

const plainContent = computed(() =>
  editor.value ? ProsemirrorHelper.getInnerTextContent(editor.value.state).trim() : ''
);

const updateValidationData = () => {
  if (plainContent.value === '') {
    emit('update', plainContent.value);
    emit('validate-form-input', !isRequired);
    emailErrorMessage.value = isRequired
      ? t('input_fields_error_field_not_correctly_filled_out')
      : '';
    return;
  }
  const isValid = validateEmail(plainContent.value);
  emit('validate-form-input', isValid);
  emailErrorMessage.value = isValid ? '' : t('input_fields_error_field_not_correctly_filled_out');
  if (isValid) {
    emit('update', plainContent.value);
  }
};

const update = () => {
  if (!editable) {
    return;
  }

  updateValidationData();
};

const debounceUpdate = useDebounceFn(update, 300);

watch(
  () => fieldFocused,
  () => {
    if (fieldFocused) {
      editor.value?.view.focus();
    }
  }
);

watch(
  () => recipientInput,
  () => {
    if (!recipientInput || !editor.value) {
      return;
    }

    const content = ProsemirrorHelper.getInputContent(recipientInput?.value || '', fields);
    editor.value?.commands.setContent(content);
  }
);

watch(
  () => editable,
  () => {
    editor.value?.setEditable(editable);
  }
);

const initialContent = () => {
  const field = EditorHelper.hasMergeTags(inputField?.value || '')
    ? ProsemirrorHelper.getMergeValueFromMergeTag(
        ProsemirrorHelper.getTagIdFromMergeTag(inputField?.value || ''),
        fields,
        mergeValues
      )
    : inputField?.value;
  const inputValue = recipientInput?.value || field || '';
  return ProsemirrorHelper.getInputContent(inputValue, fields);
};

const editorConfig = computed(() => {
  const errorId = errorMessageId.value;
  const inputIdValue = inputId.value;
  const hasErrorValue = hasError.value;

  const describedByParts = [
    ...(ariaDescribedby ? [ariaDescribedby] : []),
    ...(hasErrorValue ? [errorId] : []),
  ].filter(Boolean);

  const attributes: Record<string, string> = {
    role: editable ? 'textbox' : 'presentation',
  };

  if (id) {
    attributes.id = inputIdValue;
  }

  if (editable && ariaLabelledby) {
    attributes['aria-labelledby'] = ariaLabelledby;
  }

  if (editable && describedByParts.length > 0) {
    attributes['aria-describedby'] = describedByParts.join(' ');
  }

  if (editable && isRequired) {
    attributes['aria-required'] = 'true';
  }

  if (editable && hasErrorValue) {
    attributes['aria-invalid'] = 'true';
  }

  return {
    content: initialContent(),
    editable: editable,
    enableInputRules: false,
    enablePasteRules: false,
    extensions: [
      InputDocNode,
      Focus,
      ParagraphNode,
      Text,
      MergeValueNode,
      Placeholder.configure({
        placeholder: t('input_fields_add_email'),
      }),
    ],
    onBlur: update,
    onUpdate: debounceUpdate,
    editorProps: {
      attributes,
    },
  };
});

onMounted(() => {
  editor.value = new TipTapEditor(editorConfig.value);
});

onBeforeUnmount(() => {
  editor.value?.destroy();
});
</script>
<style lang="scss" scoped>
@import '@getaccept/editor-lib/src/scss/form-input-editor';

.input-editor-container {
  --input-transition-speed: 0.2s;

  font-family: var(--default-font-family);
  display: block;
  width: 100%;
  color: var(--text-dark);
  position: relative;

  .email-input-container {
    min-height: 2.5rem;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: var(--spacing-50);
    width: 100%;
    padding: calc(#{var(--spacing-75)} - 1px);
    color: var(--editor-input-field-text-color);
    background: var(--gray-98);
    border-radius: var(--radius-medium);
    border: 1px solid var(--gray-89);
    transition: box-shadow var(--input-transition-speed);

    &:focus-within {
      outline: 2px solid var(--info-dark);
      outline-offset: 2px;
    }

    .icon-left {
      flex-shrink: 0;
    }

    .email-input {
      width: 100%;
    }
  }

  .updating-spinner {
    position: absolute;
    right: var(--spacing-50);
    top: calc(#{var(--spacing-50)} + 1px);
    width: 20px;
  }

  &.error {
    border: none !important;

    .email-input-container {
      border-color: var(--error-dark);
    }
  }

  .error-message {
    color: var(--error-dark);
    margin-top: var(--spacing-25);
  }

  .email-input-container:not(.editable):focus-within {
    outline: none;
  }
}
</style>
